import { useContext, useEffect, useRef } from "react";
import { globalVariables } from "helper/globalVariables";
import { pencilAlt } from "helper/icons";
import { ContactDetails } from "./components/ContactDetails";
import { StatsCard } from "./components/StatsCard";
import Notes from "./components/Notes";
import { useNavigate, useParams } from "react-router-dom";
import { AddNoteModal } from "shared/Components/addNotes";
import { useMutation, useQuery } from "@apollo/client";

import Loader from "shared/Components/loader";
import {
  GetCommonUserDetail,
  UpdateNotes,
} from "redux/features/superAdminSlice/manageUsersSlice/manageUsersGql";
import { ROLE } from "helper/utility";
import {
  RoleTabContext,
  RoleTabDispatchContext,
} from "context/RoleTabsContext";
import MemberCalender from "./components/Member/MemberCalender";
import HostCalender from "./components/Host/HostCalender";
import TabHeader from "../Dashboard/Tabs/TabHeader";
import JudgeApprovalComponent from "./components/Judge";
import PageInfo from "shared/Components/pageInfo";

export const UserDetail = () => {
  const currentRole = useContext(RoleTabContext);
  const setRole = useContext(RoleTabDispatchContext);
  const [updateNotesCallBack, { loading: btnLoading }] =
    useMutation(UpdateNotes);

  const { id } = useParams();
  const { data, loading, refetch } = useQuery(GetCommonUserDetail, {
    variables: { authId: id },
    fetchPolicy: "no-cache",
  });
  // console.log(data, "SKSK");

  useEffect(() => {
    if (data) {
      setRole(
        data?.getCommonUserDetail?.role.length === 0
          ? ROLE.Member
          : data?.getCommonUserDetail?.role[0]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const userData = data?.getCommonUserDetail || {};
  const userRoles = [...new Set(data?.getCommonUserDetail.role)] || [];
  const addNoteModalRef = useRef();
  const navigate = useNavigate();

  function handleClick() {
    navigate("/admin/manage/users");
  }
  
  const onAddNotes = async (notes, callBack) => {
    await updateNotesCallBack({ variables: { authId: id, notes: notes } });
    callBack();
    refetch();
  };

  const onEditHandler = () => {
    if (currentRole === ROLE.Judge) {
      navigate(`/admin/manage/users/${id}/Judge/update`);
    } else if (currentRole === ROLE.Host) {
      navigate(`/admin/manage/users/${id}/Host/update`);
    } else {
      navigate(`/admin/manage/users/${id}/Member/update`);
    }
  };

  return (
    <div className="App lg:rounded-md min-h-screen lg:ml-[17rem] sm:pt-8 bg-white lg:p-10">
      {loading ? (
        <Loader />
      ) : (
        <>
          <PageInfo 
            title={
              <>
                {userData?.name} <span className="text-base">{`(${currentRole})`}</span>
              </>
            }
            onClick={handleClick}>
          </PageInfo>
          <div className="flex flex-col md:p-0 p-4 gap-4">
            {userRoles?.length > 1 && (
              <div className="flex gap-4 w-max max-[380px]:w-[100%] max-[380px]:justify-center mb-4">
                {userRoles?.map((rol, index) => (
                  <TabHeader
                    key={index}
                    onClick={() => {
                      setRole(rol);
                    }}
                    heading={rol}
                    isActive={currentRole === rol}
                  />
                ))}
              </div>
            )}
            {currentRole !== ROLE.SuperAdmin && (
              <div className="flex justify-end">
                <img className="cursor-pointer" onClick={onEditHandler} src={pencilAlt} alt="" />
              </div>
            )}
            <div className="flex flex-col rounded-md shadow-100 p-4">
              <div className="text-xl font-semibold border-b-2">
                {globalVariables.contactDetails}
              </div>
              <div className="flex flex-col md:flex-row gap-4  justify-between">
                <ContactDetails
                  userDetail={{
                    email: userData?.email ?? "-",
                    phoneNumber: userData?.phone_number ?? "(000) 000-0000",
                    address: userData?.location_address ?? "-",
                    role: currentRole,
                    level: userData?.leve ?? "-",
                    distanceTravel: userData?.distance_travel ?? 0,
                    zipCode: userData?.zipCode ?? "-",
                    city: userData?.city ?? "-",
                    state: userData?.state ?? "-",
                  }}
                />
                <div className="flex flex-col gap-4 p-4">
                  <div className="text-md font-bold">Emergency Contact</div>
                  <div className="flex flex-row gap-2">
                    <div className="text-sm font-medium">Name:</div>
                    <div>{userData?.emergency_contact_name ?? "N/A"}</div>
                  </div>
                  <div className="flex flex-row gap-2">
                    <div className="text-sm font-medium">Number:</div>
                    <div>{userData?.emergency_contact_number ?? "N/A"}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  {currentRole === ROLE.Host && (
                    <div className="flex flex-col gap-4">
                      <StatsCard
                        Title={globalVariables.numberOfTrailHosted}
                        stats={userData?.noofTrialsHosted ?? 0}
                      />
                      <StatsCard
                        Title={globalVariables.numberOfUpComingTask}
                        stats={userData?.noOfUpcomingTrials ?? 0}
                      />
                    </div>
                  )}
                  {currentRole === ROLE.Member && (
                    <StatsCard
                      Title={globalVariables.numberOfTrailParticipated}
                      stats={userData?.noOfTrialsParticipated ?? 0}
                    />
                  )}
                  {currentRole === ROLE.Judge && (
                    <StatsCard
                      Title={globalVariables.numberOfTrailJudge}
                      stats={userData?.noOfTrialsJudged ?? 0}
                      customHeight={"h-[48%]"}
                    />
                  )}
                  </div>
                  <Notes
                    text={userData?.notes === null ? "" : userData?.notes}
                    onClick={() => addNoteModalRef.current?.openModal()}
                    customHeight={currentRole === ROLE.Judge ? "h-[48%]" : ""}
                  />
              </div>
            </div>
          </div>
          {currentRole === ROLE.Member && <MemberCalender role={currentRole} authId={id}/>}
          {currentRole === ROLE.Host && <HostCalender role={currentRole} />}
          {currentRole === ROLE.Judge && (
            <JudgeApprovalComponent defaulLevel={userData?.level} />
          )}
        </>
      )}

      <AddNoteModal
        ref={addNoteModalRef}
        title={
          userData?.notes === ""
            ? globalVariables.addNote
            : globalVariables.updateNote
        }
        onUpdate={(notes, callBack) => onAddNotes(notes, callBack)}
        btnLoading={btnLoading}
        currentNote={userData?.notes}
      />
    </div>
  );
};
